import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relic/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API_URL}/api/admin/relic/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-2xl font-bold mb-6">Relic Settings</h1>

      <form onSubmit={handleSave} className="bg-white border rounded-lg p-6 space-y-6">
        <div>
          <label className="block text-sm font-medium mb-1">
            Base URL for QR Codes (Read-Only)
          </label>
          <input
            type="text"
            value="https://planeskeeper.com/relics"
            className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-600"
            readOnly
            disabled
          />
          <p className="text-xs text-gray-500 mt-1">
            QR codes will link to: https://planeskeeper.com/relics?c={'{CODE}'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Allowed Character Set
          </label>
          <input
            type="text"
            value={settings?.allowed_charset || ''}
            onChange={(e) => setSettings({ ...settings, allowed_charset: e.target.value })}
            className="w-full border rounded-lg px-3 py-2 font-mono"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Characters used for code generation (excludes ambiguous: O, I, L, 0, 1)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Rate Limit per IP (per day)
          </label>
          <input
            type="number"
            min="1"
            value={settings?.rate_limit_per_ip || 5}
            onChange={(e) => setSettings({ ...settings, rate_limit_per_ip: parseInt(e.target.value) })}
            className="w-full border rounded-lg px-3 py-2"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum claim attempts per IP address per day
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={settings?.checksum_enabled || false}
            onChange={(e) => setSettings({ ...settings, checksum_enabled: e.target.checked })}
            className="rounded"
          />
          <label className="text-sm font-medium">
            Enable Checksum Validation
          </label>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>
      </form>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Code Format Information</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Format: XXX-XXX (3 characters, hyphen, 3 characters)</li>
          <li>• Total length: 7 characters including hyphen</li>
          <li>• Character set excludes: O, I, L, 0, 1 (to avoid confusion)</li>
          <li>• Example: ABC-9KQ, TH7-M4R</li>
        </ul>
      </div>
    </div>
  );
};
